import React, { useState, useParams, useEffect } from 'react';
import '../../App.scss';
import 'antd/dist/antd.css';
import {
  List, Button, Space, Card, Checkbox, Radio, Form, Input, Modal, Typography, Table, Tabs, Tag, Tooltip, Empty, Divider,
  Badge, Avatar, Switch, Collapse, message as antdMessage,
} from 'antd';
const { Panel } = Collapse;
const { Text, Paragraph } = Typography;
import {
  PlusCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  CheckCircleOutlined,  
 } from '@ant-design/icons';

const { TabPane } = Tabs;
import Spinner from '../Spinner';
import { useSessionStorage } from '../../models/useSessionStorage';
import { useSitesByType } from '../../models/useSites';
import { capitalize } from '../../utils/ProductSwitcher';
import { colorForBackgroundColor } from '../../utils/RGBColorToHex';
import { SiteImageEditor } from '../client/UploadableImageEditor';

import { ColorPickerControlFormItem } from './AdminSitesColorPicker';

const isLocalHost = window.location.hostname === 'localhost';


const ExternalLink = ({ url }) => {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="link-primary text-decoration-underline">
      {url}
    </a>
  );
}

const EditReviewRequiredFields = ({ review, isApp }) => {
  return (
    <>
      <Form.Item
        name="brand"
        label="Unique Identifier"
        style={{ width: '100%', textAlign: 'left' }}
        rules={[
          {
            required: true,
            message: 'Please provide a unique identifier no spaces',
            whitespace: false,
            pattern: /^[a-zA-Z0-9]+$/,
          },
        ]}
      >
        <Input maxLength={22} />
      </Form.Item>
      <Form.Item
        name="displayName"
        label={isApp ? "App Name" : "Display Name"}
        style={{ width: '100%', textAlign: 'left' }}
        rules={[
          {
            required: true,
            message: `Please provide a ${isApp ? 'app name' : 'display name'}`,
          },
        ]}
      >
        <Input maxLength={64} />
      </Form.Item>
      <Form.Item
        name="description"
        label={isApp ? "Call to Action / Headline" : "Description"}
        style={{ width: '100%', textAlign: 'left' }}
        rules={[
          {
            required: true,
            message: isApp ? 'Please provide a headline' : 'Please provide a description',
          },
        ]}
      >
        <Input maxLength={128} placeholder={isApp ? "e.g., Get our mobile app" : ""} />
      </Form.Item>
    </>
  );
}

const EditReviewOptionalFields = ({ review, isApp }) => {
  return (
    <>
      <Form.Item
        name="link"
        label={isApp ? "App Store link" : "Site Main Page"}
        style={{ width: '100%', textAlign: 'left' }}
        placeholder="https://"
        rules={[
          {
            required: false,
            message: 'Please provide a valid https:// url',
            pattern: /^(https):\/\/[^ "]+$/, // https url link
          },
        ]}
      >
        <Input maxLength={220} />
      </Form.Item>
      <Form.Item
        name="button"
        label="Button Text"
        style={{ width: '100%', textAlign: 'left' }}
      >
        <Input maxLength={24} placeholder={isApp ? "Download" : "Visit"} />
      </Form.Item>
    </>
  );
}

const RowFooterLineItem = ({ imageUrl, displayName, isApp, brandColor }) => {
  return (
    <div className="d-flex align-items-center pt-1 px-0">
      <div className="me-2">
        <Avatar src={imageUrl} size={16} shape="square" style={{ borderRadius: isApp ? '10%' : '4px' }} />
      </div>
      <div className='pt-1 px-1'>
        <Text type="secondary" style={{ lineHeight: "24px", display: "inline-flex", alignItems: "center" }}>
          {displayName}
          {isApp && <Tag color="blue" className="mx-2" style={{ fontSize: '10px', lineHeight: '16px' }}>App</Tag>}
        </Text>
      </div>
    </div>
  );
};

const SiteDisplay = ({ item }) => {
  const { isApp, type, displayName, description, imageUrl, button, rgbColor, link, brand } = item;
  const brandColor = rgbColor || '#ff0098';
  const contrastColor = colorForBackgroundColor(brandColor);

  const showIcon = imageUrl || type === 'social';
  const titleName = isApp ? (description || displayName) : displayName;
  const descriptionText = isApp ? null : description;

  return (
    <List.Item.Meta
      title={
        <div className='row h3 justify-content-left mx-1 align-items-center'>
            {titleName}
            <div className='mx-3'><Tag color={brandColor}>{brand}</Tag></div>
        </div>
      }
      description={
        <ul className='mx-1' style={{ listStyle: 'none', padding: 0 }}>
          {descriptionText && <li>{descriptionText}</li>}
          <li className="mt-2">
            <Button 
              size="small"
              style={{ backgroundColor: brandColor, borderColor: brandColor }}
              href={link}
              target="_blank"
            >
              <span style={{ color: contrastColor }}>{button || (isApp ? 'Download' : (type === 'social' ? 'Follow' : 'Visit'))}</span>
            </Button>
          </li>
          <li className="mt-1"><ExternalLink url={link} /></li>
          {displayName && (link || isApp) && <li><RowFooterLineItem imageUrl={imageUrl} displayName={displayName} isApp={isApp} brandColor={brandColor}/></li>}
        </ul>
      }
      avatar={showIcon ? <img src={imageUrl} style={{ width: 'auto', height: '90px', padding: '16px', minWidth: '90px', objectFit: 'contain', borderRadius: isApp ? '10%' : '4px' }} /> : null}
    />
  );
};

const CreateSiteForm = ({ onCreate, onUpdate, editId, editItem, errorMessage, square, rectangle, type }) => {
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState((editItem && editItem.imageUrl) || null);
  const [isApp, setIsApp] = useState((editItem && editItem.isApp) || false);
  const modalCtaCopy = editId ? 'Save' : 'Create';
  const { rgbColor } = editItem || {};

  useEffect(() => {
    if (isApp) {
      form.setFieldsValue({ button: 'Download' });
    } else if (type === 'social' && (!editId || !form.getFieldValue('button'))) {
      form.setFieldsValue({ button: 'Follow' });
    } else if (!editId && !isApp && type !== 'social') {
      form.setFieldsValue({ button: 'Visit' });
    }
  }, [isApp, type, editId, form]);

  const onFinish = (values) => {
    values.link = values.link || null;
    values.imageUrl = values.imageUrl || null;
    values.button = values.button || null;
    values.rgbColor = values.rgbColor || null;
    values.isApp = isApp;

    if (isApp && !imageUrl && !values.imageUrl) {
      antdMessage.error('App Icon (Image) is mandatory for Mobile App Links');
      return;
    }

    if (editId) {
      values.id = editId;
      values.imageUrl = imageUrl || editItem.imageUrl || values.imageUrl;
      onUpdate && onUpdate(values);
      return;
    }
    onCreate && onCreate(values);
  };

  return (
      <div style={{ textAlign: 'center' }}>
        <Card>
          {
            errorMessage &&
            <div className='alert alert-danger' role='alert'>
              {errorMessage}
            </div>
          }
          <Form
            form={form}
            name="createSite"
            onFinish={onFinish}
            initialValues={{ ...editItem }}
          >
            <Form.Item
              name="isApp"
              label="Mobile App Link"
              valuePropName="checked"
              style={{ textAlign: 'left' }}
            >
              <Switch checked={isApp} onChange={(checked) => setIsApp(checked)} />
            </Form.Item>
            <SiteImageEditor 
              siteId={editId}
              square={!!square}
              rectangle={!!rectangle}
              currentUrl={(editItem && editItem.imageUrl) || null}
              onUpdateImage={(imageUrl) => setImageUrl(imageUrl)}
            />
            <EditReviewRequiredFields isApp={isApp} />
            <ColorPickerControlFormItem title='Pick a color for the review' rgbColor={rgbColor} />
            <EditReviewOptionalFields isApp={isApp} />
            <Form.Item>
              <Button type="primary" htmlType="submit">
                {modalCtaCopy}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
  );
}

const SiteStatusButton = ({ item, onClickUpdateStatus, loadingButton }) => {
  return (
    <Button
      type="primary"
      style={{
        backgroundColor: item.status === 'live' ? 'green' : 'grey',
        borderColor: item.status === 'live' ? 'green' : 'grey',
      }}
      onClick={() => onClickUpdateStatus(item)}
      icon={loadingButton.loading && loadingButton.id === item.id ? <LoadingOutlined /> : null}
    >
      {capitalize(item.status)}
    </Button>
  );
}



const SiteTypeTab = ({ reviews, createSite, updateSite, filterByStatus, status, type, square, rectangle }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [message, setMessage] = useState(null);
  const [loadingButton, setLoadingButton] = useState({ loading: false, id: null });
  const [editId, setEditId] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const titles = {
    review: 'Review Site',
    affiliate: 'Affiliate Site',
    ad: 'Ad Site',
    loyalty: 'Loyalty Site',
    social: 'Social Site',
  };
  const title = titles[type] || 'Review Site';
  const modalTitle = editId ? `Edit ${title}` : `Create ${title}`;
  const selectedStatus = status || 'clear';

  const onClickModalCancel = () => {
    setMessage(null);
    setShowCreateModal(false);
    setEditId(null);
    setEditItem(null);
  }

  const onCreate = async (values) => {
    setMessage(null);
    const {
      brand,
      displayName,
      description,
    } = values || {};

    if (!brand || !displayName || !description) {
      setMessage('Missing required fields');
      return;
    }

    const newReview = await createSite({ ...values, type });

    if (!newReview) {
      setMessage('Brand needs to be unique');
      return;
    }

    setMessage(null);
    setShowCreateModal(false);
  }

  const onUpdate = async (values) => {
    setMessage(null);
    const {
      id,
      brand,
      displayName,
      description,
    } = values || {};

    if (!id || !brand || !displayName || !description) {
      setMessage('Missing required fields');
      return;
    }

    await updateSite(id, { ...values, type });

    setEditId(null);
    setEditItem(null);
    setMessage(null);
    setShowCreateModal(false);
  }

  const onClickFilter = (e) => {
    let status = e.target.value;
    status = status === 'clear' ? null : status;
    filterByStatus && filterByStatus(status, type);
  }

  const onClickUpdateStatus = async (item) => {
    const { id, status } = item;
    const statuses = ['live', 'paused', 'created'];
    if (!statuses.includes(status)) {
      return;
    }
    const flipStatuses = {
      live: 'paused',
      paused: 'live',
      created: 'live',
    };

    // Normalize item to prevent undefined fields from breaking Firestore
    const normalizedItem = {
      ...item,
      link: item.link || null,
      imageUrl: item.imageUrl || null,
      button: item.button || null,
      rgbColor: item.rgbColor || null,
      isApp: !!item.isApp,
    };

    setLoadingButton({ loading: true, id });
    const newStatus = flipStatuses[status];
    await updateSite(id, { ...normalizedItem, status: newStatus });
    setLoadingButton({ loading: false, id: null });
  }

  const onClickEdit = (item) => {
    const { id } = item;
    setEditId(id);
    setEditItem(item);
    setShowCreateModal(true);
  }

  return (
    <div>
      <div className='row'>
        <div className='col text-left'>
          <div className='container'>
            <Radio.Group onChange={onClickFilter} defaultValue={selectedStatus} buttonStyle={'outline'} size={'small'}>
              <Radio.Button value="created">Created</Radio.Button>
              <Radio.Button value="paused">Paused</Radio.Button>
              <Radio.Button value="live">Live</Radio.Button>
              { selectedStatus !== 'clear' && <Radio.Button value="clear"><CloseCircleOutlined /></Radio.Button> }
            </Radio.Group>
          </div>
        </div>
        <div className='col text-right'>
        <div className='container'>
            <Button
              type="primary"
              onClick={() => setShowCreateModal(true)} icon={<PlusCircleOutlined />}
              style={{ backgroundColor: 'green', borderColor: 'green' }}
              size='small'
            >
              {'Create'}
            </Button>
          </div>
        </div>
      </div>

      <Modal
        title={modalTitle}
        visible={showCreateModal}
        onCancel={() => onClickModalCancel()}
        footer={null}
        bodyStyle={{ minHeight: '200px', minWidth: '280px' }}
        destroyOnClose={true}
      >
        <CreateSiteForm
          onCreate={onCreate}
          onUpdate={onUpdate}
          editId={editId}
          editItem={editItem}
          onCancel={onClickModalCancel}
          errorMessage={message}
          square={!!square}
          rectangle={!!rectangle}
        />
      </Modal>

      <Divider />

      <List
        itemLayout="horizontal"
        pagination={{
          pageSize: 8,
        }}
        style={{ textAlign: 'left' }}
        dataSource={reviews}
        split={true}
        renderItem={item => (
          <List.Item
            style={{ padding: '8px 0' }}
            actions={[
              <SiteStatusButton item={item} onClickUpdateStatus={onClickUpdateStatus} loadingButton={loadingButton} />,
              <Button onClick={() => onClickEdit(item)}>{`Edit`}</Button>,
            ]}
          >
            <SiteDisplay item={item} />
          </List.Item>
        )}
      />
    </div>
  );
}



const AdminAllSitesBody = ({ isAdmin, ...rest }) => {
  const [activeTab, setActiveTab] = useSessionStorage('adminAllSitesTab', 'reviews');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [statuses, setStatuses] = useState({});
  const reviewType = 'review';
  const [reviews, createReview, updateReview] = useSitesByType({  status: statuses.review, type: reviewType });
  const affiliateType = 'affiliate';
  const [affiliates, createAffiliate, updateAffiliate] = useSitesByType({ status: statuses.affiliate, type: affiliateType });
  const adType = 'ad';
  const [ads, createAd, updateAd] = useSitesByType({ status: statuses.ad, type: adType });
  const bookType = 'book';
  const [books, createBook, updateBook] = useSitesByType({ status: statuses.book, type: bookType });
  const loyaltyType = 'loyalty';
  const [loyalties, createLoyalty, updateLoyalty] = useSitesByType({ status: statuses.loyalty, type: loyaltyType });
  const socialType = 'social';
  const [socials, createSocial, updateSocial] = useSitesByType({ status: statuses.social, type: socialType });

  const filterByStatus = (status, type) => {
    setStatuses(
      statuses => ({
          ...statuses,
          [type]: status,
          })
    );
  }

  const tabsList = [
    { id: 'reviews', tab: 'Reviews', create: createReview, update: updateReview, data: reviews, type: 'review', square: true },
    { id: 'bookings', tab: 'Booking', create: createBook, update: updateBook, data: books, type: 'book', square: true }, 
    { id: 'affiliates', tab: 'Shopping', create: createAffiliate, update: updateAffiliate, data: affiliates, type: 'affiliate', square: true },
    { id: 'loyalties', tab: 'Loyalty', create: createLoyalty, update: updateLoyalty, data: loyalties, type: 'loyalty', square: true },
    { id: 'social', tab: 'Social', create: createSocial, update: updateSocial, data: socials, type: 'social', square: true },
    { id: 'ads', tab: 'Ads', create: createAd, update: updateAd, data: ads, type: 'ad', square: true },
  ];

  return (
    (isAdmin) ?
      <>
        {isMobile ? (
          <Collapse 
            accordion 
            defaultActiveKey={[activeTab]} 
            onChange={(key) => setActiveTab(Array.isArray(key) ? key[0] : key)}
            expandIconPosition="right"
            style={{ border: 'none', background: 'white' }}
          >
            {tabsList.map(({ id, tab, create, update, data, type, square, rectangle }) => (
              <Panel 
                header={
                  <span className="h4" style={{ margin: 0 }}>{tab}</span>
                } 
                key={id}
                style={{ borderBottom: '1px solid #f0f0f0' }}
              >
                <div className='p-0 m-0 w-100'>
                  {data && 
                    <SiteTypeTab 
                      reviews={data}
                      createSite={create}
                      updateSite={update}
                      filterByStatus={filterByStatus}
                      status={statuses[type] || null}
                      type={type}
                      square={!!square}
                      rectangle={!!rectangle}
                    />
                  }
                  <Spinner condition={() => !data} />
                </div>
              </Panel>
            ))}
          </Collapse>
        ) : (
          <Tabs defaultActiveKey={activeTab} type='card' onChange={v => setActiveTab(v)}>
            { tabsList.map(({ id, tab, create, update, data, type, square, rectangle }) => (
              <TabPane tab={tab} key={id} style={{ padding: '0px' }}>
                <div className='p-0 m-0 h-100 w-100'>
                  {data && 
                    <SiteTypeTab 
                      reviews={data}
                      createSite={create}
                      updateSite={update}
                      filterByStatus={filterByStatus}
                      status={statuses[type] || null}
                      type={type}
                      square={!!square}
                      rectangle={!!rectangle}
                    />
                  }
                  <Spinner condition={() => !data} />
                </div>
              </TabPane>
            ))
            }
          </Tabs>
        )}
      </>

      :
      <Paragraph>
        You are not authorised to view this page.
      </Paragraph>
  );
};


export default AdminAllSitesBody;